import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { candidatePortalAPI } from '../services/api';
import './JobDetails.css';

const formatDate = (val) => {
  if (!val) return null;
  const d = new Date(val);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const daysUntil = (val) => {
  if (!val) return null;
  const d = new Date(val);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
};

const formatSalary = (r) => {
  if (!r || (!r.min && !r.max)) return null;
  const fmt = (n) =>
    n >= 100000 ? `${(n / 100000).toFixed(1)}L` : n >= 1000 ? `${(n / 1000).toFixed(0)}K` : String(n);
  const cur = r.currency || 'INR';
  return `${cur} ${fmt(r.min)} – ${cur} ${fmt(r.max)}`;
};

const JobDetails = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (!jobId) { setError('No job ID provided'); setLoading(false); return; }
    const load = async () => {
      try {
        setLoading(true);
        const res = await candidatePortalAPI.getJobDetails(jobId);
        // backend returns { success, data: job }
        const jobData = res.data?.data ?? (res.data?._id ? res.data : null);
        if (jobData) setJob(jobData);
        else setError('Job not found');
      } catch (e) {
        setError('Failed to load job details.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [jobId]);

  const copyShareLink = () => {
    const url = `${window.location.origin}/jobs/${jobId}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    });
  };

  if (loading) return (
    <div className="job-details-loading">
      <div className="loading-spinner" />
      <h2>Loading Opportunity…</h2>
      <p>Discover your next career move with Solitaire Overseas</p>
    </div>
  );

  if (error || !job) return (
    <div className="job-details-error">
      <div className="error-icon">⚠️</div>
      <h3>Opportunity Not Found</h3>
      <p>{error || 'This role may have already been filled.'}</p>
      <button onClick={() => navigate('/')} className="btn btn-back" style={{ width: 'auto' }}>
        🔍 Browse All Opportunities
      </button>
    </div>
  );

  const days = daysUntil(job.applicationDeadline);
  const isExpired = days !== null && days < 0;
  const isUrgent = days !== null && days >= 0 && days <= 7;
  const statusLabel = isExpired ? 'Closed' : isUrgent ? 'Closing Soon' : 'Active';
  const statusCls = isExpired ? 'expired' : isUrgent ? 'urgent' : 'active';
  const salary = formatSalary(job.salaryRange);
  const deadline = formatDate(job.applicationDeadline);

  const InfoRow = ({ icon, label, value }) => (
    <div className="info-row">
      <span className="info-icon">{icon}</span>
      <div>
        <span className="info-label">{label}</span>
        <span className="info-value">{value || '—'}</span>
      </div>
    </div>
  );

  return (
    <div className="job-details-container">
      {/* Hero */}
      <div className="job-hero">
        <div className="job-hero-inner">
          <button className="back-nav" onClick={() => navigate('/')}>← Back to Careers</button>

          <div className="job-hero-content">
            <div className="job-hero-info">
              <h1 className="job-hero-title">{job.jobTitle || 'Position'}</h1>
              <div className="job-hero-meta">
                {job.department && <span className="meta-badge"><span className="meta-badge-icon">🏢</span>{job.department}</span>}
                {job.location && <span className="meta-badge"><span className="meta-badge-icon">📍</span>{job.location}</span>}
                {job.employmentType && <span className="meta-badge"><span className="meta-badge-icon">⏰</span>{job.employmentType}</span>}
                {job.vacancy && <span className="meta-badge"><span className="meta-badge-icon">👥</span>{job.vacancy} position{job.vacancy !== 1 ? 's' : ''}</span>}
              </div>
              <span className={`job-status-badge ${statusCls}`}>{statusLabel}</span>
            </div>

            {deadline && (
              <div className="job-hero-actions">
                <div className="deadline-card">
                  <span className="deadline-label">Application Deadline</span>
                  <span className="deadline-date">{deadline}</span>
                  {days !== null && !isExpired && (
                    <div className="deadline-days">{days === 0 ? 'Today!' : `${days} day${days !== 1 ? 's' : ''} left`}</div>
                  )}
                  {isExpired && <div className="deadline-days" style={{ color: '#ff7c7c' }}>Closed</div>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="job-details-main">
        <div className="job-details-layout">

          {/* Left: content */}
          <div>
            {/* Description */}
            <div className="job-content-section">
              <h2 className="section-title"><span className="section-title-icon">🎯</span> About the Role</h2>
              {job.description
                ? job.description.split('\n').filter(l => l.trim()).map((line, i) => (
                    <p key={i} className="section-text" style={{ marginBottom: 10 }}>{line.trim()}</p>
                  ))
                : <p className="section-text" style={{ color: '#aaa' }}>No description provided for this role.</p>
              }
            </div>

            {/* Requirements */}
            {job.requirements?.length > 0 && (
              <div className="job-content-section">
                <h2 className="section-title"><span className="section-title-icon">📋</span> Requirements</h2>
                <ul className="section-list">
                  {job.requirements.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}

            {/* Responsibilities */}
            {job.responsibilities?.length > 0 && (
              <div className="job-content-section">
                <h2 className="section-title"><span className="section-title-icon">🎯</span> Key Responsibilities</h2>
                <ul className="section-list">
                  {job.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}

            {/* Skills */}
            {job.skillsRequired?.length > 0 && (
              <div className="job-content-section">
                <h2 className="section-title"><span className="section-title-icon">🛠️</span> Required Skills</h2>
                <div className="skills-tags">
                  {job.skillsRequired.map((s, i) => <span key={i} className="skill-tag">{s}</span>)}
                </div>
              </div>
            )}

            {/* Benefits */}
            {job.benefits?.length > 0 && (
              <div className="job-content-section">
                <h2 className="section-title"><span className="section-title-icon">🎁</span> Benefits &amp; Perks</h2>
                <ul className="section-list">
                  {job.benefits.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            )}
          </div>

          {/* Right: sidebar */}
          <div className="job-sidebar">
            {/* Apply card */}
            <div className="sidebar-card apply-card">
              <h3 className="section-title">Ready to Apply?</h3>
              <p>Take the next step in your career journey with Solitaire Overseas.</p>
              <button
                onClick={() => navigate(`/jobs/${jobId}/apply`)}
                disabled={isExpired}
                className="btn btn-primary-white"
              >
                {isExpired ? '❌ Applications Closed' : '⭐ Apply Now'}
              </button>
              <button onClick={copyShareLink} className="btn btn-ghost">
                {linkCopied ? '✅ Link Copied!' : '🔗 Copy Job Link'}
              </button>
            </div>

            {/* Job details */}
            <div className="sidebar-card">
              <h3 className="section-title"><span className="section-title-icon">📊</span> Job Details</h3>
              <InfoRow icon="📍" label="Location" value={job.location} />
              <InfoRow icon="🏢" label="Department" value={job.department} />
              <InfoRow icon="💼" label="Designation" value={job.designation?.name || (typeof job.designation === 'string' ? null : null)} />
              <InfoRow icon="⏰" label="Employment Type" value={job.employmentType} />
              <InfoRow icon="👥" label="Vacancies" value={job.vacancy ? `${job.vacancy} open position${job.vacancy !== 1 ? 's' : ''}` : null} />
              {job.experienceRequired && (
                <InfoRow icon="📈" label="Experience" value={`${job.experienceRequired.min ?? 0}–${job.experienceRequired.max ?? 0} years`} />
              )}
              {salary && (
                <div className="info-row">
                  <span className="info-icon">💰</span>
                  <div>
                    <span className="info-label">Salary Range</span>
                    <span className="info-value" style={{ color: '#667eea', fontWeight: 700 }}>{salary}</span>
                  </div>
                </div>
              )}
              <div className="info-row" style={{ borderBottom: 'none' }}>
                <span className="info-icon">📅</span>
                <div>
                  <span className="info-label">Application Deadline</span>
                  <span className="info-value">{deadline || 'Not specified'}</span>
                  {days !== null && !isExpired && days >= 0 && (
                    <span style={{ fontSize: 12, color: isUrgent ? '#e65100' : '#28a745', fontWeight: 600, display: 'block', marginTop: 2 }}>
                      {days === 0 ? 'Closes today!' : `${days} day${days !== 1 ? 's' : ''} remaining`}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Company */}
            <div className="sidebar-card">
              <h3 className="section-title"><span className="section-title-icon">🏢</span> About Solitaire Overseas</h3>
              <p className="section-text" style={{ fontSize: 13 }}>
                Solitaire Overseas Private Limited is a dynamic organization dedicated to building global careers and creating opportunities worldwide.
              </p>
              <p className="section-text" style={{ fontSize: 13, marginTop: 10 }}>
                <strong>Our Mission:</strong> To empower professionals through international exposure and career growth.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetails;
